import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useChannels, useCreateChannel, useDeleteChannel, useTestChannel, useUpdateChannel } from "@/api/notify";
import type { ChannelEvent } from "@/api/types/ChannelEvent";
import type { ChannelKind } from "@/api/types/ChannelKind";
import type { ChannelView } from "@/api/types/ChannelView";
import type { Project } from "@/api/types/Project";
import type { Severity } from "@/api/types/Severity";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { useToast } from "@/components/Toast";
import { Button, Checkbox, Chip, Field, Input, LoadError, Select, Spinner } from "@/components/ui";
import { relativeTime } from "@/lib/format";

const EVENTS: ChannelEvent[] = [
  "bug_reported",
  "bug_closed",
  "task_ready_for_test",
  "task_ready_for_retest",
  "round_finished",
  "task_passed",
];
const SEVERITIES: Severity[] = ["s1", "s2", "s3", "s4"];

/** Slack / Telegram team announcements for a project (project managers). */
export function ChannelsSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const channels = useChannels(project.key);
  const [editing, setEditing] = useState<ChannelView | "new" | null>(null);

  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="m-0 label-caps">{t("channels.title")}</h3>
        {!project.archived && (
          <Button size="sm" icon="plus" onClick={() => setEditing("new")}>
            {t("channels.add")}
          </Button>
        )}
      </div>
      <p className="m-0 text-sm text-muted">{t("channels.intro")}</p>

      {channels.isPending ? (
        <Spinner />
      ) : channels.isError ? (
        <LoadError onRetry={() => void channels.refetch()} />
      ) : channels.data.length === 0 ? (
        <p className="m-0 text-sm text-muted">{t("channels.empty")}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {channels.data.map((c) => (
            <li key={c.id}>
              <ChannelRow project={project} channel={c} onEdit={() => setEditing(c)} />
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ChannelDialog
          project={project}
          channel={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function ChannelRow({ project, channel: c, onEdit }: { project: Project; channel: ChannelView; onEdit: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateChannel(project.key);
  const test = useTestChannel(project.key);
  const remove = useDeleteChannel(project.key);
  const [confirm, setConfirm] = useState(false);
  const ok = c.last_status === "ok";

  const sendTest = () =>
    test.mutate(c.id, {
      onSuccess: (res) => {
        const r = res as ChannelView;
        if (r.last_status === "ok") toast.success(t("channels.testSent"));
        else toast.error(`${t("channels.testFailed")}: ${r.last_status ?? ""}`);
      },
      onError: (e) => toast.error(e),
    });

  return (
    <div className="flex flex-col gap-2.5 rounded-[var(--radius-ctl)] border border-border p-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="inline-flex size-8 items-center justify-center rounded-md bg-surface-2 text-text-2">
          <Icon name={c.kind === "slack" ? "message" : "send"} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{c.name}</span>
          <span className="block truncate font-mono text-xs text-muted">
            {t(`channels.kinds.${c.kind}`)} · {c.target_hint}
          </span>
        </span>
        <Checkbox
          label={c.enabled ? t("channels.enabled") : t("channels.disabled")}
          checked={c.enabled}
          disabled={update.isPending || project.archived}
          onChange={(e) =>
            update.mutate(
              { id: c.id, body: { enabled: e.target.checked } },
              { onSuccess: () => toast.success(t("channels.saved")), onError: (err) => toast.error(err) },
            )
          }
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {c.events.map((ev) => (
          <Chip key={ev}>{t(`channels.eventNames.${ev}`)}</Chip>
        ))}
        {c.events.some((ev) => ev.startsWith("bug_")) && (
          <Chip tone="neutral">{t(`channels.severityOptions.${c.min_severity}`)}</Chip>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={`flex-1 text-xs ${c.last_status && !ok ? "text-fail-fg" : "text-muted"}`}>
          {!c.last_status
            ? t("channels.never")
            : ok
              ? t("channels.lastOk", { time: c.last_sent_at ? relativeTime(c.last_sent_at) : "" })
              : t("channels.lastError", { error: c.last_status })}
        </span>
        {!project.archived && (
          <>
            <Button size="sm" icon="send" loading={test.isPending} onClick={sendTest}>
              {t("channels.sendTest")}
            </Button>
            <Button size="sm" icon="edit" onClick={onEdit}>
              {t("channels.edit")}
            </Button>
            <Button size="sm" variant="ghost" icon="trash" aria-label={t("channels.remove")} onClick={() => setConfirm(true)} />
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title={t("channels.removeTitle", { name: c.name })}
        body={t("channels.removeBody")}
        confirmLabel={t("channels.remove")}
        danger
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(c.id, {
            onSuccess: () => {
              setConfirm(false);
              toast.success(t("channels.removed"));
            },
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function ChannelDialog({ project, channel, onClose }: { project: Project; channel: ChannelView | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreateChannel(project.key);
  const update = useUpdateChannel(project.key);
  const isNew = channel === null;

  const [kind, setKind] = useState<ChannelKind>(channel?.kind ?? "slack");
  const [name, setName] = useState(channel?.name ?? "");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [events, setEvents] = useState<ChannelEvent[]>(channel?.events ?? ["bug_reported", "task_ready_for_test", "task_ready_for_retest"]);
  const [minSeverity, setMinSeverity] = useState<Severity>(channel?.min_severity ?? "s2");
  const [replace, setReplace] = useState(isNew);
  const [tried, setTried] = useState(false);

  const credsOk = kind === "slack" ? webhookUrl.trim() !== "" : botToken.trim() !== "" && chatId.trim() !== "";
  const valid = name.trim() !== "" && events.length > 0 && (!replace || credsOk);
  const busy = create.isPending || update.isPending;

  const toggle = (ev: ChannelEvent, on: boolean) =>
    setEvents((cur) => (on ? EVENTS.filter((x) => x === ev || cur.includes(x)) : cur.filter((x) => x !== ev)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!valid) return;
    const creds =
      kind === "slack"
        ? { webhook_url: webhookUrl.trim(), bot_token: null, chat_id: null }
        : { webhook_url: null, bot_token: botToken.trim(), chat_id: chatId.trim() };
    const done = {
      onSuccess: () => {
        toast.success(t(isNew ? "channels.created" : "channels.saved"));
        onClose();
      },
      onError: (err: unknown) => toast.error(err),
    };
    if (isNew) {
      create.mutate({ kind, name: name.trim(), events, min_severity: minSeverity, ...creds }, done);
    } else {
      update.mutate(
        {
          id: channel.id,
          body: {
            name: name.trim(),
            events,
            min_severity: minSeverity,
            enabled: null,
            ...(replace ? creds : { webhook_url: null, bot_token: null, chat_id: null }),
          },
        },
        done,
      );
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? t("channels.add") : t("channels.edit")}
      width={560}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form="channel-form" loading={busy}>
            {isNew ? t("channels.add") : t("common.save")}
          </Button>
        </>
      }
    >
      <form id="channel-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {isNew && (
          <Field label={t("channels.kind")}>
            {(p) => (
              <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as ChannelKind)}>
                <option value="slack">{t("channels.kinds.slack")}</option>
                <option value="telegram">{t("channels.kinds.telegram")}</option>
              </Select>
            )}
          </Field>
        )}
        <Field label={t("channels.name")} error={tried && !name.trim() ? t("channels.required") : undefined}>
          {(p) => <Input {...p} value={name} placeholder={t("channels.namePlaceholder")} onChange={(e) => setName(e.target.value)} />}
        </Field>

        {!isNew && <Checkbox label={t("channels.replaceCredentials")} checked={replace} onChange={(e) => setReplace(e.target.checked)} />}
        {replace &&
          (kind === "slack" ? (
            <Field
              label={t("channels.webhookUrl")}
              hint={isNew ? t("channels.webhookHint") : t("channels.credentialsHint")}
              error={tried && !webhookUrl.trim() ? t("channels.required") : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="url"
                  autoComplete="off"
                  value={webhookUrl}
                  placeholder="https://hooks.slack.com/services/…"
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              )}
            </Field>
          ) : (
            <>
              <p className="m-0 rounded-md bg-surface-2 px-3 py-2.5 text-sm whitespace-pre-line text-text-2">{t("channels.telegramHelp")}</p>
              <Field label={t("channels.botToken")} error={tried && !botToken.trim() ? t("channels.required") : undefined}>
                {(p) => (
                  <Input {...p} autoComplete="off" value={botToken} placeholder="123456:ABC-DEF…" onChange={(e) => setBotToken(e.target.value)} />
                )}
              </Field>
              <Field label={t("channels.chatId")} error={tried && !chatId.trim() ? t("channels.required") : undefined}>
                {(p) => <Input {...p} autoComplete="off" value={chatId} placeholder="-1001234567890" onChange={(e) => setChatId(e.target.value)} />}
              </Field>
            </>
          ))}

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1.5 text-sm font-medium text-text-2">{t("channels.events")}</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EVENTS.map((ev) => (
              <Checkbox key={ev} label={t(`channels.eventNames.${ev}`)} checked={events.includes(ev)} onChange={(e) => toggle(ev, e.target.checked)} />
            ))}
          </div>
          {tried && events.length === 0 && <span className="text-xs text-fail-fg">{t("channels.pickEvent")}</span>}
        </fieldset>

        {events.some((ev) => ev.startsWith("bug_")) && (
          <Field label={t("channels.minSeverity")}>
            {(p) => (
              <Select {...p} value={minSeverity} onChange={(e) => setMinSeverity(e.target.value as Severity)}>
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {t(`channels.severityOptions.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </form>
    </Dialog>
  );
}
